/**
 * Katalog nomining ko'p tilli ko'rinishi — zaxira zanjiri BITTA joyda.
 *
 * ⚠️ Backenddagi `uz.xsib.catalog.shared.i18n.LocalizedName` bilan AYNAN
 * bir xil qoida. Ikki nusxa ataylab: panel ro'yxatni bir marta oladi va
 * har qatorni o'zi chizadi — har nom uchun serverdan so'rash mumkin emas.
 * Qoida esa BIR JOYDA yozilgan bo'lishi kerak, aks holda panel bilan
 * kabinet bir xil paketni har xil nomlab qo'yardi.
 *
 * Zanjir — o'zgarmas tartib:
 *   1. `nameI18n[til]` bor va bo'sh EMAS  -> o'sha
 *   2. aks holda                          -> `name` (kanonik)
 *
 * Ya'ni: til yo'q -> `name`; xarita bo'sh -> `name`; bo'sh matn -> `name`.
 * Javob HECH QACHON bo'sh qaytmaydi.
 */

/** Panelda tahrirlanadigan tillar — forma shu ro'yxat bo'yicha chiziladi. */
export const NAME_LANGUAGES = ['uz', 'ru', 'en'] as const

export type NameLanguage = (typeof NAME_LANGUAGES)[number]

export interface Named {
  name: string
  nameI18n: Record<string, string>
}

/**
 * Til kodini solishtirishga tayyorlaydi: `RU`, ` ru `, `ru-RU` -> `ru`.
 *
 * ⚠️ Mintaqa qismi tashlanadi: brauzer `ru-RU` beradi, operator `ru` deb
 * yozadi — ular bir til va ikkitasini ayri kalit qilish tarjimani
 * jimgina yo'qotardi.
 */
function normalizeKey(language: string | null | undefined): string {
  if (!language) return ''
  const trimmed = language.trim().toLowerCase()
  const cut = trimmed.search(/[-_]/)
  return cut < 0 ? trimmed : trimmed.slice(0, cut)
}

/**
 * Shu tilda tarjima BORMI — ro'yxatdagi belgichaga kerak.
 *
 * ⚠️ `!!nameI18n[lang]` yetarli emas: bo'sh satr ham «bor» bo'lib
 * ko'rinardi, ekranda esa zaxiraga tushardi.
 */
export function hasTranslation(item: Named, language: string | null | undefined): boolean {
  const key = normalizeKey(language)
  if (!key) return false
  const value = item.nameI18n[key]
  return typeof value === 'string' && value.trim() !== ''
}

/** So'ralgan tildagi nom; topilmasa — kanonik `name`. Bo'sh QAYTMAYDI. */
export function displayName(item: Named, language: string | null | undefined): string {
  const key = normalizeKey(language)
  const translated = key ? item.nameI18n[key] : undefined
  if (typeof translated === 'string' && translated.trim() !== '') {
    return translated.trim()
  }
  return item.name
}

/** Amal qatoridan paket haqida keladigan HAMMA narsa */
export interface PlanRef {
  planId: string | null
  /** Serverning KANONIK nomi — bitta tilda, tarjimasiz */
  planName: string | null
}

/**
 * «Berilgan paketlar» qatorining paket nomi.
 *
 * ⚠️ TARTIB O'ZGARMAS va u SERVERNIKIDAN OLDIN mahalliy katalogni
 * so'raydi:
 *   1. `planId` katalogda topildi -> {@link displayName}, ya'ni
 *      FOYDALANUVCHI TILIDAGI nom;
 *   2. topilmadi -> `planName`, serverning kanonik nomi (bitta tilda);
 *   3. ikkalasi ham yo'q -> `null`.
 *
 * Nega aynan shu tartib: `/api/catalog/plans` da `nameI18n` bor va
 * kassir uni kassa ekranida AYNI shu tilda ko'rgan. Serverning
 * `planName` i esa kanonik — ruscha panelda o'zbekcha nom chiqarardi,
 * ya'ni bitta paket ikki ekranda ikki xil atalardi.
 *
 * ⚠️ `null` — «nom yo'q», «Noma'lum paket» EMAS. To'qima matn yozish
 * serverning ataylab qilmagan ishini panelda qaytarish bo'lardi;
 * chaqiruvchi bo'shlikni o'z konteksti bo'yicha ko'rsatadi.
 *
 * ⚠️ Katalogdagi yozuv nomi BO'SH bo'lsa zanjir 2-bosqichga TUSHADI —
 * bitta buzuq katalog yozuvi qatorni bo'sh qoldirmasin.
 */
export function grantPlanLabel(
  ref: PlanRef,
  catalog: ReadonlyMap<string, Named>,
  language: string | null | undefined,
): string | null {
  const known = ref.planId === null ? undefined : catalog.get(ref.planId)
  if (known) {
    const shown = displayName(known, language).trim()
    if (shown !== '') return shown
  }
  const canonical = ref.planName?.trim()
  return canonical ? canonical : null
}

/**
 * Siyosat TANLASH ro'yxatining yorlig'i — nom tarjima bilan, yonida tezlik.
 *
 * ⚠️ NEGA ALOHIDA FUNKSIYA VA NEGA U KERAK BO'LDI (o'lchangan).
 * `PlansPage.tsx` jadvalidagi siyosat ustuni tarjimani chizadi va u
 * yerdagi izoh sababini ochiq yozadi: «aks holda bitta ekranda paket
 * ruscha, uning siyosati o'zbekcha turardi». Ayni sahifadan ochiladigan
 * paket formasidagi siyosat tanlash ro'yxati esa `policy.name` ni xom
 * ko'rsatardi — ya'ni o'sha izoh ogohlantirgan holat bir qavat pastda
 * qolgan edi: operator jadvalda «Тариф 100 Мбит» ni ko'rib, tahrirlash
 * oynasini ochganda ro'yxatda `tarif-100M` ni topardi.
 *
 * Yorliq shu yerda quriladi, JSX ichida emas — aks holda uni faqat
 * manba naqshi bilan tekshirib bo'lardi, zaxira zanjiri esa
 * BAJARILADIGAN test talab qiladi.
 *
 * Tezlik qavs ichida qoladi: siyosatlar ko'pincha aynan tezlik bilan
 * farqlanadi va faqat nom ko'rsatish tanlovni ko'r qilardi.
 */
export function policyOptionLabel(
  policy: Named & { rateLimit?: string | null },
  language: string | null | undefined,
): string {
  const shown = displayName(policy, language)
  return policy.rateLimit ? `${shown} (${policy.rateLimit})` : shown
}

/**
 * Formadagi qiymatlarni backendga yuboriladigan shaklga keltiradi.
 *
 * Bo'sh maydonlar TASHLANADI — backend ham xuddi shunday qiladi
 * (`LocalizedName.normalize`), lekin ularni yubormaslik so'rovni
 * o'qishga qulay qoldiradi va «yozdim, lekin yo'q» degan chalkashlikni
 * kamaytiradi.
 */
export function toNameI18n(
  values: Partial<Record<NameLanguage, string | undefined>>,
): Record<string, string> {
  const clean: Record<string, string> = {}
  for (const lang of NAME_LANGUAGES) {
    const value = values[lang]
    if (typeof value === 'string' && value.trim() !== '') {
      clean[lang] = value.trim()
    }
  }
  return clean
}
